import {
  type MemberEntitlementsResponse,
  type PromoGrantCreateRequest,
  type UserEntitlement,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

const ENTITLEMENTS = '/admin/entitlements';

/** Revoking needs the member as well as the row — see {@link entitlementsApi}. */
export interface RevokeEntitlementArgs {
  id: string;
  /**
   * Whose entitlement it is.
   *
   * Not sent: the route names the entitlement and `EntitlementRevokeSchema` is
   * `.strict()`, so a stray key is a 422. It is here because the **cache** is
   * keyed by member — there is no list route to invalidate, only one member's
   * answer — and because a refusal leaves no response to read it off.
   */
  userId: string;
  reason: string;
}

/**
 * What one member is entitled to, and the two staff writes that move it (P23b3b).
 *
 * ## Its own api, not endpoints bolted onto `paymentsApi`
 *
 * `paymentsApi` defines itself as the **direct** channel — orders a human entered
 * and decided. An entitlement can come from any of four stores, and three of them
 * that api has never heard of. They invalidate nothing of each other's, so
 * sharing a cache would put two unrelated resources behind one set of tags;
 * `subscriptionsApi` and `billingEventsApi` are already separate for the same
 * reason, "rather than a billing super-api".
 *
 * ## The cache is keyed by MEMBER, because there is no list
 *
 * The api offers no route over all entitlements — the surface is a member picker
 * and that member's rows, which is also the support question. So the tag carries
 * the `userId`, and a write invalidates exactly the one member it touched rather
 * than every member anybody has looked at.
 *
 * ## It does not write the response into the cache
 *
 * Both writes answer with the entitlement. Rendering from that answer would give
 * the screen two sources for one member's rows — the response, and the list —
 * and they disagree the moment a grant *replaces* rather than adds: the write
 * returns one row where the list would show that the open-ended comp underneath
 * it is gone. Re-reading is the only rendering that can be right.
 *
 * ## Nothing polls
 *
 * Unlike the billing trail, these rows change only when a human or a webhook acts
 * on them, and the surface is opened to answer a question rather than lived in.
 * What keeps it current is tag invalidation for its own writes and
 * `billing.order.changed` for somebody else's (W5).
 */
export const entitlementsApi = createApi({
  reducerPath: 'entitlementsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['MemberEntitlements'],
  endpoints: (builder) => ({
    memberEntitlements: builder.query<MemberEntitlementsResponse, string>({
      query: (userId) => ({ url: `${ENTITLEMENTS}/member/${userId}`, method: 'GET' }),
      providesTags: (_result, _error, userId) => [
        { type: 'MemberEntitlements' as const, id: userId },
      ],
    }),

    /**
     * Comps an account — App Review, an internal tester, a goodwill gesture.
     *
     * The body is the contract's own request type and is passed through
     * untouched, because **an absent `durationDays` and a `null` one mean
     * opposite things**: the service reads `'durationDays' in input`, so omitting
     * the key asks for the product's own duration while `null` asks for a comp
     * with no end. `JSON.stringify` drops an `undefined` property, which is what
     * carries "not chosen" across the wire as an absent key — a fact about the
     * serializer that no type can express, so `entitlements.api.spec` pins it.
     *
     * Building the body here instead would mean this file deciding a default, and
     * a default is exactly what collapses the two.
     */
    grantPromo: builder.mutation<UserEntitlement, PromoGrantCreateRequest>({
      query: (body) => ({ url: `${ENTITLEMENTS}/promo`, method: 'POST', body }),
      invalidatesTags: (_result, _error, body) => [
        { type: 'MemberEntitlements' as const, id: body.userId },
      ],
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceEntitlementChange(queryFulfilled.then(({ data }) => data));
      },
    }),

    /**
     * Takes an entitlement back — a refund, a mistaken order, a comp withdrawn.
     *
     * Nothing retries and nothing swallows the refusal:
     * `billing_entitlement_not_claimable` is what a second revoke produces, and it
     * means the row already ended. Re-stamping `revokedAt` would misreport when
     * access actually ended, which is the fact a billing dispute turns on — so
     * the api does nothing and the caller says so.
     */
    revokeEntitlement: builder.mutation<UserEntitlement, RevokeEntitlementArgs>({
      query: ({ id, reason }) => ({
        url: `${ENTITLEMENTS}/${id}/revoke`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: 'MemberEntitlements' as const, id: userId },
      ],
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceEntitlementChange(queryFulfilled.then(({ data }) => data));
      },
    }),
  }),
});

/**
 * Announces the change on the client bus (W5).
 *
 * The **member** is read off the response rather than off the argument: it is the
 * row the api actually wrote, and on a grant that replaced an existing comp the
 * response is the only thing that knows which row survived.
 */
async function announceEntitlementChange(landed: Promise<UserEntitlement>): Promise<void> {
  try {
    const entitlement = await landed;

    EVENT_BUS.publish({
      type: 'billing.entitlement.changed',
      userId: entitlement.userId,
      entitlementId: entitlement.id,
    });
  } catch {
    // Refused — nothing to announce. Swallowed deliberately: `onQueryStarted` has
    // no caller, so a rejection escaping it becomes an unhandled rejection that
    // exits the runner non-zero beside a wholly green suite (P22b1). The failure
    // is already the caller's, through `unwrap()`.
  }
}

export const { useMemberEntitlementsQuery, useGrantPromoMutation, useRevokeEntitlementMutation } =
  entitlementsApi;
